import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { SimpleIcon, TPP_LOGO_URL } from '@adthlete/common'
import { toast } from '@/components/ui/sonner'

const getSocialName = (platform: string) => {
  switch (platform) {
    case 'instagram':
      return 'Instagram'
    case 'facebook':
      return 'Facebook'
    case 'youtube':
      return 'YouTube'
    case 'tiktok':
      return 'TikTok'
    default:
      return platform.charAt(0).toUpperCase() + platform.slice(1)
  }
}

const getSocialProfileUrl = (account: any) => {
  switch (account.platform) {
    case 'instagram':
      return `https://www.instagram.com/${account.platform_account_display_name}`
    case 'youtube':
      return `https://www.youtube.com/channel/${account.platform_account_id}`
    case 'tiktok':
      return `https://www.tiktok.com/@${account.platform_account_display_name}`
    case 'facebook':
      // Facebook pages can use either ID or username, prefer username if available
      return account.platform_account_display_name
        ? `https://www.facebook.com/${account.platform_account_display_name}`
        : `https://www.facebook.com/profile.php?id=${account.platform_account_id}`
    default:
      return null
  }
}

interface SocialMediaSettingsProps {
  athlete: any
  isWelcome: boolean
  disconnectSocialMedia: (platform: string) => Promise<void>
}

const SocialMediaSettings = ({
  athlete,
  isWelcome,
  disconnectSocialMedia,
}: SocialMediaSettingsProps) => {
  const navigate = useNavigate()
  const [disconnectingPlatform, setDisconnectingPlatform] = useState<string | null>(null)

  const socialPlatforms = [
    {
      name: 'Instagram',
      slug: 'instagram',
      color: 'from-[#FF3A89] to-[#FFC702]',
      platform: 'instagram',
    },
    { name: 'TikTok', slug: 'tiktok', color: 'bg-black', platform: 'tiktok' },
    { name: 'YouTube', slug: 'youtube', color: 'bg-[#FF0000]', platform: 'youtube' },
  ]

  const handleSocialConnect = (platform: string) => {
    if (platform === 'instagram') {
      // Navigate to Instagram connect page instead of dummy data
      navigate('/connect/instagram')
    } else if (platform === 'tiktok') {
      // Navigate to TikTok connect page
      navigate('/connect/tiktok')
    } else if (platform === 'youtube') {
      // Navigate to YouTube connect page
      navigate('/connect/youtube')
    } else {
      // No more dummy behavior needed
      toast.error('Platform not supported', {
        description: `${platform} integration is not yet available.`,
      })
    }
  }

  const handleSocialDisconnect = async (platform: string) => {
    setDisconnectingPlatform(platform)
    try {
      await disconnectSocialMedia(platform)
      toast('Disconnected', { description: 'Your social account has been disconnected.' })
    } catch (err: any) {
      toast.error('Failed to disconnect', {
        description: err.message || 'Please try again.',
      })
    } finally {
      setDisconnectingPlatform(null)
    }
  }

  return (
    <>
      {isWelcome && (
        <Card className="bg-linear-to-r from-[#C0FD72]/80 via-[#C0FD72]/60 to-[#a8e45a]/70 backdrop-blur-xs shadow-xl rounded-3xl rounded-bl-none overflow-hidden border border-[#C0FD72]/50 mb-6">
          <CardHeader>
            <CardTitle className="text-black font-bold text-xl">Welcome to Athios!</CardTitle>
            <CardDescription className="text-gray-800 font-medium">
              Great! Your account has been created successfully. To get the most out of Athios and
              start receiving collaboration opportunities, connect at least one social media
              account below.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      <Card variant="dark" className="overflow-hidden">
        <CardHeader>
          <CardTitle className="text-white">Social Media Accounts</CardTitle>
          <CardDescription className="text-gray-400">
            Connect your account to streamline campaign reporting to eliminate an extra step.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* TPP Branding Banner */}
          <div className="bg-[#3A4E5C]/50 border border-[#5DBED9]/30 rounded-xl p-4 mb-6 flex items-start gap-4">
            <div className="shrink-0 bg-white rounded-lg p-1 w-16 h-16 flex items-center justify-center">
              <img
                src={TPP_LOGO_URL}
                alt="Talent Pitch Pro"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <p className="text-white text-sm font-medium mb-1">Powered by Talent Pitch Pro</p>
              <p className="text-gray-300 text-xs leading-relaxed">
                We use our sister company, <span className="text-[#5DBED9]">Talent Pitch Pro</span>
                , to securely connect your social accounts. When you connect a platform, you will
                be redirected to their verification page.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {socialPlatforms.map((platform) => {
              const linkedAccount = athlete.socialAccounts.find(
                (acc: any) => acc.platform === platform.platform,
              )
              // An account linked from public data at signup isn't connected until the athlete
              // authorizes it
              const isConnected = !!linkedAccount?.oauth_connected
              const hasPendingAuth = athlete.authTokenInfo?.some(
                (auth: any) => auth.platform === platform.platform,
              )

              const pendingHandle = athlete.pendingSocialHandles?.find(
                (h: any) => h.platform.toLowerCase() === platform.platform.toLowerCase(),
              )

              const isPending = !isConnected && (hasPendingAuth || !!pendingHandle)

              // If we have a pending handle but not technically "pending auth" (which implies oauth started),
              // we treat it as a prompt to connect.
              const displayText = isConnected
                ? 'Connected'
                : hasPendingAuth
                  ? 'Connection pending - click continue to finish setup'
                  : linkedAccount
                    ? `Connect to verify ${linkedAccount.platform_account_display_name || 'your account'}`
                    : pendingHandle
                      ? `Pending verification for ${pendingHandle.handle}`
                      : `Connect your ${platform.name} account`

              return (
                <div key={platform.platform} className="flex items-center gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`${platform.color} p-2 rounded-full shrink-0`}>
                      <SimpleIcon slug={platform.slug} height={20} width={20} color="white" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-white">{platform.name}</p>
                      <p className="text-sm text-gray-400 truncate">{displayText}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleSocialConnect(platform.platform)}
                    disabled={isConnected}
                    variant={isConnected ? 'dark-outline' : 'lime'}
                    className="shrink-0"
                  >
                    {isConnected ? 'Connected' : hasPendingAuth ? 'Continue setup' : 'Connect'}
                  </Button>
                </div>
              )
            })}

            {athlete.socialAccounts.length > 0 && (
              <div className="pt-4 border-t border-white/10 mt-6">
                <h3 className="text-white font-medium mb-3">Your Connected Accounts</h3>
                {athlete.socialAccounts.map((account: any) => {
                  const profileUrl = getSocialProfileUrl(account)
                  return (
                    <div key={account.id} className="flex items-center gap-3 py-2">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="min-w-0">
                          <p className="font-medium text-white">
                            {getSocialName(account.platform)}
                          </p>
                          <div className="flex items-center gap-2">
                            {account.platform === 'youtube' ? (
                              // For YouTube, only show the link since the ID is not user-friendly
                              profileUrl && (
                                <a
                                  href={profileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#C0FD72] hover:text-[#d2ff9c] text-sm underline"
                                >
                                  View Channel
                                </a>
                              )
                            ) : (
                              // For other platforms, show username and link
                              <>
                                <p className="text-sm text-gray-400">
                                  {account.platform_account_display_name}
                                </p>
                                {profileUrl && (
                                  <a
                                    href={profileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#C0FD72] hover:text-[#d2ff9c] text-sm underline"
                                  >
                                    View Profile
                                  </a>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleSocialDisconnect(account.platform)}
                        disabled={disconnectingPlatform === account.platform}
                        className="shrink-0"
                      >
                        {disconnectingPlatform === account.platform
                          ? 'Disconnecting...'
                          : 'Disconnect'}
                      </Button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  )
}

export default SocialMediaSettings
